/**
 * Map state management hooks using React Query cache for state sharing.
 *
 * Pattern:
 * - Page components write to cache using `useSetMap*` hooks
 * - PersistentSearchLayout reads from cache using `useMap*` hooks
 * - All read hooks use useQuery to automatically re-render on cache updates
 * - State persists across route changes (SRP ↔ HDP navigation)
 *
 * @example
 * // In page component: Write map state
 * const setMapPosition = useSetMapPosition();
 * setMapPosition(bounds, center, zoom);
 *
 * @example
 * // In layout: Read map state (auto-updates on changes)
 * const mapPosition = useMapPosition();
 */

import { useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useCallback } from 'react';
import {
  type ActiveListingState,
  type MapBorderState,
  type MapDetectedAreaState,
  type MapMarker,
  type MapPositionSource,
  type MapState,
  mapStateKeys,
  type NearMeMapContextState,
  type PendingAreaNavigationState,
  type PendingListingNavigationState,
} from './mapStateKeys';
import type { BoundingRect } from '@/src/components/features/search/Map/types';
import type { AreaResponse } from '@/src/lib/api/endpoints/area';

/**
 * Writes map position (bounds, center, zoom) to React Query cache.
 */
export function useSetMapPosition() {
  const queryClient = useQueryClient();

  return useCallback(
    (
      bounds: BoundingRect,
      center: { lat: number; lng: number },
      zoom: number,
      source: MapPositionSource = 'map',
      areaKey?: string,
    ) => {
      const existing = queryClient.getQueryData<MapState>(mapStateKeys.position());
      queryClient.setQueryData<MapState>(mapStateKeys.position(), {
        bounds,
        center,
        zoom,
        source,
        areaKey: areaKey ?? existing?.areaKey,
      });
    },
    [queryClient],
  );
}

/**
 * Clears stale map presentation state before navigating to another area.
 * The current position stays mounted as a visual placeholder until the next
 * SRP's MapStateHydrator seeds the destination position/boundary.
 */
export function usePrepareMapForAreaNavigation() {
  const queryClient = useQueryClient();

  return useCallback((targetResourceId?: string) => {
    const existingBorder = queryClient.getQueryData<MapBorderState>(mapStateKeys.border());

    if (targetResourceId) {
      queryClient.setQueryData<PendingAreaNavigationState>(mapStateKeys.pendingAreaNavigation(), {
        targetResourceId,
      });
    }

    queryClient.setQueryData<Array<MapMarker>>(mapStateKeys.markers(), []);
    queryClient.setQueryData<string | null>(mapStateKeys.pinnedMarker(), null);
    queryClient.setQueryData<string | null>(mapStateKeys.hoveredMarker(), null);
    queryClient.setQueryData<ActiveListingState | null>(mapStateKeys.activeListing(), null);
    queryClient.setQueryData<MapDetectedAreaState>(mapStateKeys.detectedArea(), { detectedArea: null });

    if (existingBorder) {
      queryClient.setQueryData<MapBorderState>(mapStateKeys.border(), {
        showBorder: existingBorder.showBorder,
        isDetecting: false,
        encodedBoundary: null,
        isBoundaryLoading: existingBorder.showBorder,
        boundaryAreaId: null,
      });
    }
  }, [queryClient]);
}

/**
 * Marks a listing navigation that starts outside the current search context and
 * clears the prior map's presentation state. HdpHydrator consumes the marker to
 * replace the stale area and viewport with the selected listing's server-resolved state.
 */
export function usePrepareMapForListingNavigation() {
  const queryClient = useQueryClient();
  const prepareMapForAreaNavigation = usePrepareMapForAreaNavigation();

  return useCallback(
    (sourceListingId: string) => {
      queryClient.setQueryData<PendingListingNavigationState>(mapStateKeys.pendingListingNavigation(), {
        sourceListingId,
      });
      prepareMapForAreaNavigation();
      queryClient.removeQueries({ queryKey: mapStateKeys.position(), exact: true });
    },
    [prepareMapForAreaNavigation, queryClient],
  );
}

export function useClearPendingMapAreaNavigation() {
  const queryClient = useQueryClient();

  return useCallback(
    (targetResourceId?: string) => {
      const pending = queryClient.getQueryData<PendingAreaNavigationState>(
        mapStateKeys.pendingAreaNavigation(),
      );
      if (!pending) return;
      if (targetResourceId && pending.targetResourceId !== targetResourceId) return;

      queryClient.setQueryData<PendingAreaNavigationState | null>(
        mapStateKeys.pendingAreaNavigation(),
        null,
      );
    },
    [queryClient],
  );
}

export function usePendingListingNavigation(): PendingListingNavigationState | null {
  const { data } = useQuery<PendingListingNavigationState>({
    queryKey: mapStateKeys.pendingListingNavigation(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? null;
}

// React Query commits queryFn results asynchronously. For map state, these
// hooks should only subscribe to cache writes from useSetMap* callers; running a
// queryFn can race a real setQueryData write and replace it with a fallback.
const NEVER_QUERY_FN = (): never => {
  throw new Error('NEVER_QUERY_FN should not be called; map state hooks subscribe to cache writes only.');
};

/**
 * Reads map position from React Query cache.
 * Re-renders component when position changes.
 */
export function useMapPosition(): MapState | undefined {
  const { data } = useQuery<MapState>({
    queryKey: mapStateKeys.position(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity, // Never mark as stale
    gcTime: Infinity, // Never garbage collect
  });

  return data;
}

/**
 * Writes map markers to React Query cache.
 */
export function useSetMapMarkers() {
  const queryClient = useQueryClient();

  return useCallback(
    (markers: Array<MapMarker>) => {
      queryClient.setQueryData<Array<MapMarker>>(mapStateKeys.markers(), markers);
    },
    [queryClient],
  );
}

/**
 * Reads map markers from React Query cache.
 * Re-renders component when markers change.
 */
export function useMapMarkers(): Array<MapMarker> {
  const { data } = useQuery<Array<MapMarker>>({
    queryKey: mapStateKeys.markers(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? [];
}

/**
 * Writes map border state to React Query cache.
 */
export function useSetMapBorderState() {
  const queryClient = useQueryClient();

  return useCallback(
    (borderState: MapBorderState) => {
      queryClient.setQueryData<MapBorderState>(mapStateKeys.border(), borderState);
    },
    [queryClient],
  );
}

/**
 * Reads map border state from React Query cache.
 * Re-renders component when border state changes.
 */
export function useMapBorderState(): MapBorderState {
  const { data } = useQuery<MapBorderState>({
    queryKey: mapStateKeys.border(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const borderState = data ?? {
    showBorder: true,
    isDetecting: false,
    encodedBoundary: null,
    isBoundaryLoading: false,
    boundaryAreaId: null,
  };

  return borderState;
}

/**
 * Writes detected area to React Query cache.
 */
export function useSetMapDetectedArea() {
  const queryClient = useQueryClient();

  return useCallback(
    (detectedArea: AreaResponse | null) => {
      queryClient.setQueryData<MapDetectedAreaState>(mapStateKeys.detectedArea(), {
        detectedArea,
      });
    },
    [queryClient],
  );
}

/**
 * Reads detected area from React Query cache.
 * Re-renders component when detected area changes.
 */
export function useMapDetectedArea(): AreaResponse | null {
  const { data } = useQuery<MapDetectedAreaState>({
    queryKey: mapStateKeys.detectedArea(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data?.detectedArea ?? null;
}

/** Writes the near-me map context (upgrade slug + geolocated area) to cache. */
export function useSetNearMeMapContext() {
  const queryClient = useQueryClient();

  return useCallback(
    (context: NearMeMapContextState | null) => {
      queryClient.setQueryData<NearMeMapContextState | null>(mapStateKeys.nearMeContext(), context);
    },
    [queryClient],
  );
}

/**
 * Reads the near-me map context from cache. `null` on ordinary SRP/HDP routes.
 */
export function useNearMeMapContext(): NearMeMapContextState | null {
  const { data } = useQuery<NearMeMapContextState | null>({
    queryKey: mapStateKeys.nearMeContext(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? null;
}

export function usePendingMapAreaNavigation(): PendingAreaNavigationState | null {
  const { data } = useQuery<PendingAreaNavigationState | null>({
    queryKey: mapStateKeys.pendingAreaNavigation(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? null;
}

/**
 * Writes pinned marker ID to React Query cache.
 * Used by page components to request a marker be pinned on the map.
 */
export function useSetMapPinnedMarker() {
  const queryClient = useQueryClient();

  return useCallback(
    (markerId: string | null) => {
      queryClient.setQueryData<string | null>(mapStateKeys.pinnedMarker(), markerId);
    },
    [queryClient],
  );
}

/**
 * Reads pinned marker ID from React Query cache.
 * Re-renders component when pinned marker changes.
 */
export function useMapPinnedMarker(): string | null {
  const { data } = useQuery<string | null>({
    queryKey: mapStateKeys.pinnedMarker(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? null;
}

/**
 * Writes hovered marker ID to React Query cache.
 * Used by listing cards to highlight the corresponding map marker on hover.
 */
export function useSetMapHoveredMarker() {
  const queryClient = useQueryClient();

  return useCallback(
    (markerId: string | null) => {
      queryClient.setQueryData<string | null>(mapStateKeys.hoveredMarker(), markerId);
    },
    [queryClient],
  );
}

/**
 * Reads hovered marker ID from React Query cache.
 * Re-renders component when hovered marker changes.
 */
export function useMapHoveredMarker(): string | null {
  const { data } = useQuery<string | null>({
    queryKey: mapStateKeys.hoveredMarker(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? null;
}

/**
 * Writes active listing to React Query cache.
 * Used by HdpPage (on mount) and feed mode (scroll tracking) when implemented.
 */
export function useSetActiveListing() {
  const queryClient = useQueryClient();

  return useCallback(
    (activeListing: ActiveListingState | null) => {
      queryClient.setQueryData<ActiveListingState | null>(mapStateKeys.activeListing(), activeListing);
    },
    [queryClient],
  );
}

/**
 * Reads active listing from React Query cache.
 * Used by PersistentSearchLayout for map marker highlighting
 * and by commute hooks for route origin coordinates.
 */
export function useActiveListing(): ActiveListingState | null {
  const { data } = useQuery<ActiveListingState | null>({
    queryKey: mapStateKeys.activeListing(),
    queryFn: NEVER_QUERY_FN,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return data ?? null;
}

// Re-export mapStateKeys for external use
export { mapStateKeys };
